// promotionUtils.js
export function sumItemQty(item) {
    let sum = 0;
    for (const color of item?.order_item_colors || []) {
      for (const colorRow of [color, ...(color?.inner_colors || [])]) {
        const sizes = colorRow?.sizes || {};
        for (const sizeKey in sizes) {
          const q = parseInt(sizes[sizeKey]?.quantity, 10);
          if (!Number.isNaN(q)) sum += q;
        }
      }
    }
    return sum;
  }
  
  function isPromoEnabled(p) {
    const explicitlyDisabled = p.disabled === true || p.is_disabled === true || p.active === false;
    return !explicitlyDisabled; // ✅ enabled unless explicitly disabled
  }
  
  function getGroupItems(group) {
    // Prefer order_items; fall back to items if that’s what you have in older groups
    return group?.order_items?.length ? group.order_items : (group?.items || []);
  }

  function groupHasTechniqueType(items, techniqueTypes) {
    if (!Array.isArray(techniqueTypes) || techniqueTypes.length === 0) return true;
    return items.some(it =>
      (it?.order_item_locations || []).some(loc => {
        const t = loc?.technique?.type ?? null;
        return t && techniqueTypes.includes(t);
      })
    );
  }

  function normalizeIds(list) {
    if (!Array.isArray(list)) return [];
    return list
      .map(v => {
        if (typeof v === 'object' && v) return v.technique_id ?? v.id ?? v;
        const n = Number(v);
        return Number.isNaN(n) ? v : n;
      })
      .filter(v => v !== null && v !== undefined && v !== '');
  }

  function groupHasTechnique(items, techniqueIds) {
    const ids = normalizeIds(techniqueIds);
    if (ids.length === 0) return true;
    return items.some(it =>
      (it?.order_item_locations || []).some(loc => {
        const id = loc?.technique?.technique_id ?? null;
        return id && ids.includes(id);
      })
    );
  }

  function itemHasTechnique(item, techniqueIds, techniqueTypes) {
    const ids = normalizeIds(techniqueIds);
    const types = Array.isArray(techniqueTypes) ? techniqueTypes : [];
    if (ids.length === 0 && types.length === 0) return true;

    return (item?.order_item_locations || []).some(loc => {
      const id = loc?.technique?.technique_id ?? null;
      const type = loc?.technique?.type ?? null;
      const idOk = ids.length ? (id && ids.includes(id)) : true;
      const typeOk = types.length ? (type && types.includes(type)) : true;
      return idOk && typeOk;
    });
  }

  export function itemMatchesPromotion(item, promo) {
    if (!item || !promo) return false;
    const classList = Array.isArray(promo.product_classes) ? promo.product_classes : [];
    const productClass = item?.style_info?.product_class ?? item?.product_class ?? null;
    if (classList.length && !classList.includes(productClass)) return false;
    return itemHasTechnique(item, promo.technique_ids || [], promo.technique_types || []);
  }

  export function getFreeItemAllowanceForGroup(group, promo, allowMultiples = true) {
    if (!group || !promo) return 0;
    const min = Number(promo.min_quantity_required || 0);
    const discountValue = Number(promo.discount_value || 0);
    if (!min || !discountValue) return 0;

    const items = getGroupItems(group);
    const techniqueIds = Array.isArray(promo.technique_ids) ? promo.technique_ids : [];
    const techniqueTypes = Array.isArray(promo.technique_types) ? promo.technique_types : [];

    let qualifiedQty = 0;
    for (const it of items) {
      if (it?.promotion_applied || it?.promotion_data?.discount_type === 'free_item_same_order') {
        continue; // don't let free items qualify more free items
      }
      if (!itemHasTechnique(it, techniqueIds, techniqueTypes)) continue;
      qualifiedQty += sumItemQty(it);
    }

    if (qualifiedQty < min) return 0;
    const multiplier = allowMultiples ? Math.floor(qualifiedQty / min) : 1;
    return Math.max(0, multiplier * discountValue);
  }
  
  /**
   * Returns the eligible promos list for a group using class filtering + min qty gating.
   */
export function getEligiblePromotionsForGroup(group, prebook = null) {
    const promos = group?.program_info?.promotions || [];
    if (!promos.length) return [];
  
    const items = getGroupItems(group);
  
    const eligible = [];
    for (const promo of promos) {
      if (!isPromoEnabled(promo)) continue;
      if (prebook && !promo.allows_stackable) continue;
  
      const classList =
        Array.isArray(promo.product_classes) ? promo.product_classes :
        Array.isArray(promo.applies_to_product_classes) ? promo.applies_to_product_classes :
        null;
  
      const hasClassFilter = Array.isArray(classList) && classList.length > 0;
      let techniqueTypes = Array.isArray(promo.technique_types) ? promo.technique_types : null;
      if (typeof promo.technique_types === 'string') {
        try {
          techniqueTypes = JSON.parse(promo.technique_types);
        } catch (e) {
          techniqueTypes = promo.technique_types.split(',').map(s => s.trim()).filter(Boolean);
        }
      }
      const hasTechniqueTypeFilter = Array.isArray(techniqueTypes) && techniqueTypes.length > 0;
      if (hasTechniqueTypeFilter && !groupHasTechniqueType(items, techniqueTypes)) continue;

      let techniqueIds = Array.isArray(promo.technique_ids) ? promo.technique_ids : null;
      if (typeof promo.technique_ids === 'string') {
        try {
          techniqueIds = JSON.parse(promo.technique_ids);
        } catch (e) {
          techniqueIds = promo.technique_ids.split(',').map(s => s.trim()).filter(Boolean);
        }
      }
      const hasTechniqueIdFilter = Array.isArray(techniqueIds) && techniqueIds.length > 0;
      if (hasTechniqueIdFilter && !groupHasTechnique(items, techniqueIds)) continue;

      const scopedItems = hasClassFilter
        ? items.filter(it => {
            const pc =
              it?.style_info?.product_class ??
              it?.product_class ??
              it?.style_info?.class ??
              null;
            return pc && classList.includes(pc);
          })
        : items;
  
      if (hasClassFilter && scopedItems.length === 0) continue;
  
      const scopedQty = scopedItems.reduce((acc, it) => acc + sumItemQty(it), 0);
  
      const min = Number(promo.min_quantity_required);
      const hasMin = Number.isFinite(min);
      console.log(promo)
      console.log(min, scopedQty, hasMin);
      if (hasMin && scopedQty < min) continue;
  
      eligible.push(promo);
    }
    return eligible;
  }

  export function normalizePrebookOption(value, options = []) {
    if (!value) return '';
    const label = typeof value === 'object' ? value.pre_book_label : String(value).trim();
    if (!label) return '';
    const match = (options || []).find(option =>
      String(option.id || '') === String((value && value.id) || '') || option.pre_book_label === label
    );
    return Object.assign({}, match || (typeof value === 'object' ? value : { pre_book_label: label }));
  }

  export function getPromotionPresentation(promotion, { pilotDiagnostics = false } = {}) {
    if (!promotion) return null;

    const state = promotion.qualification_state || promotion.state || 'unknown';
    const prefix = state === 'earned'
      ? 'Earned'
      : (['qualified', 'applied'].includes(state) ? 'Qualified' : state.replace(/_/g, ' '));
    const reasonFirstStates = ['available', 'targeting', 'nearly_qualified', 'ineligible'];
    const description = reasonFirstStates.includes(state) && promotion.reason
      ? promotion.reason
      : (promotion.benefit_description || promotion.reason || 'Promotion details are unavailable.');
    const monetaryBenefitTypes = ['percentage_discount', 'free_location_amount', 'fixed_discount', 'currency_discount'];
    const amount = Number(promotion.calculated_benefit);
    const showPriceAdjustment = promotion.included_in_displayed_total === true &&
      monetaryBenefitTypes.includes(promotion.benefit_type) &&
      Number.isFinite(amount) && amount > 0;

    return {
      message: `${prefix.charAt(0).toUpperCase()}${prefix.slice(1)} — ${description}`,
      show_price_adjustment: showPriceAdjustment,
      price_adjustment: showPriceAdjustment ? amount : null,
      diagnostic: pilotDiagnostics
        ? `${promotion.pricing_treatment || 'unknown'}; displayed=${promotion.included_in_displayed_total === true}; pdf=${promotion.included_in_pdf_total === true}; netsuite=${promotion.included_in_netsuite_payload === true}`
        : null
    };
  }
  
